import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import * as Sentry from '@sentry/react';

const TenancyStatsTable = ({ period }) => {
    const { apiFetch } = useAuth();
    const [vacancyData, setVacancyData] = useState(null);
    const [prevYearVacancyData, setPrevYearVacancyData] = useState(null);
    const [retailVacancyData, setRetailVacancyData] = useState(null);
    const [prevYearRetailVacancyData, setPrevYearRetailVacancyData] = useState(null);
    const [nationalTenantStats, setNationalTenantStats] = useState(null);
    const [prevYearNationalTenantStats, setPrevYearNationalTenantStats] = useState(null);
    const [ficaComplianceStats, setFicaComplianceStats] = useState(null);
    const [prevYearFicaComplianceStats, setPrevYearFicaComplianceStats] = useState(null);
    const [vacancyFutureLetData, setVacancyFutureLetData] = useState(null);
    const [prevYearVacancyFutureLetData, setPrevYearVacancyFutureLetData] = useState(null);
    const [tenantReversionData, setTenantReversionData] = useState(null);
    const [tenantRetentionData, setTenantRetentionData] = useState(null);
    const [prevYearTenantRetentionData, setPrevYearTenantRetentionData] = useState(null);
    const [propertyIdToName, setPropertyIdToName] = useState({});

    // Extracts month name, current year, and previous year from a yyyymm period string.
    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return {
            monthName: monthNames[parseInt(month, 10)],
            year,
            prevYear
        };
    }

    const { monthName, year, prevYear } = getMonthYear(period);

    // Returns the December period of the previous year (e.g. "202512" for period "202601"). 
    const getPrevYearLastPeriod = (period) => {
        const periodStr = period.toString().padStart(6, '0');
        const year = parseInt(periodStr.substring(0, 4), 10) - 1;
        return `${year}12`;
    };

    // Fetch vacancy data for current and previous year periods.
    useEffect(() => {
        if (!period) return;

        const fetchVacancyData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/VacancyByPeriod?period=${period}&singlePeriod=true`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setVacancyData(data.data[parseInt(period, 10)] ?? null);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/VacancyByPeriod?period=${prevLastPeriod}&singlePeriod=true`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearVacancyData(data.data[parseInt(prevLastPeriod, 10)] ?? null);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchVacancyData();
    }, [apiFetch, period]);

    // Fetch retail vacancy data for current and previous year periods.
    useEffect(() => {
        if (!period) return;

        const fetchRetailVacancyData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/AverageRetailVacancyPercentage?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setRetailVacancyData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/AverageRetailVacancyPercentage?period=${prevLastPeriod}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearRetailVacancyData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchRetailVacancyData();
    }, [apiFetch, period]);

    // Fetch national tenant data for current and previous year periods.
    useEffect(() => {
        if (!period) return;

        const fetchNationalTenantsData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/NationalTenantStats?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setNationalTenantStats(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/NationalTenantStats?period=${prevLastPeriod}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearNationalTenantStats(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchNationalTenantsData();
    }, [apiFetch, period]);

    // Fetch tenant FICA compliance data for current and previous year periods.
    useEffect(() => {
        if (!period) return;

        const fetchFicaComplianceData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/FicaComplianceStats?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setFicaComplianceStats(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/FicaComplianceStats?period=${prevLastPeriod}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearFicaComplianceStats(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchFicaComplianceData();
    }, [apiFetch, period]);

    // Fetch future let vacancies
    useEffect(() => {
        if (!period) return;

        const fetchVacanciesFutureLetData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/VacancyFutureLet?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setVacancyFutureLetData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/VacancyFutureLet?period=${prevLastPeriod}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearVacancyFutureLetData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchVacanciesFutureLetData();
    }, [apiFetch, period]);


    // Fetch tenant reversion data for current and previous year periods.
    useEffect(() => {
        if (!period) return;

        const fetchTenantsetTenantReversionDataData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/TenantReversionData?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setTenantReversionData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchTenantsetTenantReversionDataData();
    }, [apiFetch, period]);

    useEffect(() => {
        if (!period) return;

        const fetchRetentionData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/TenantRetention?period=${period}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setTenantRetentionData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }

            try {
                const prevLastPeriod = getPrevYearLastPeriod(period);
                const response = await apiFetch(`/api/Mda/TenantRetention?period=${prevLastPeriod}`);
                const data = await response.json();
                if (data?.success && data.data) {
                    setPrevYearTenantRetentionData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchRetentionData();
    }, [apiFetch, period]);

    // Fetch property name lookup (name -> id), reversed to id -> name for tooltip use.
    useEffect(() => {
        const fetchPropertyNames = async () => {
            try {
                const response = await apiFetch('/api/Mda/PropertyNameToId');
                const data = await response.json();
                if (data?.success && data.data) {
                    const idToName = {};
                    Object.entries(data.data).forEach(([name, id]) => {
                        idToName[id] = name;
                    });
                    setPropertyIdToName(idToName);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };
        fetchPropertyNames();
    }, [apiFetch]);

    // Escapes a string for safe HTML rendering inside tooltips. 
    function escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    const floorToOneDecimal = (value) => (Math.floor(value * 10) / 10).toFixed(1);

    // Builds tooltip HTML listing all vacant units grouped by property for the given vacancy data. 
    function getVacancyTooltipHtml(data) {
        if (!data?.properties || data.properties.length === 0)
            return '<div>No vacancy data available</div>';

        const allVacantUnits = data.properties.flatMap(prop =>
            (prop.vacantUnits || []).map(unit => ({
                propertyName: propertyIdToName[prop.propertyID] ?? `Property ${prop.propertyID}`,
                pruNo: unit.propertyRentableUnitNo
            }))
        ).sort((a, b) => a.propertyName.localeCompare(b.propertyName));

        if (allVacantUnits.length === 0) return '<div>No vacant units</div>';

        let html = `<div style="max-width: 500px; border: 1px solid #ccc; border-radius: 4px; background: white; padding: 8px;">
            <table style="width: 100%; border-collapse: collapse;">
            <thead style="display: table; width: 100%; table-layout: fixed;">
                <tr style="background: #f5f5f5; border-bottom: 2px solid #ddd;">
                    <th style="width: 60%; padding: 8px; text-align: left; font-weight: bold; border-right: 1px solid #ddd;">Property Name</th>
                    <th style="width: 40%; padding: 8px; text-align: left; font-weight: bold;">Shop</th>
                </tr>
            </thead>
            </table>
            <div style="max-height: 300px; overflow-y: auto; display: block; border-bottom: 1px solid #ccc;">
                <table style="width: 100%; border-collapse: collapse;">
                <tbody style="display: table; width: 100%; table-layout: fixed;">`;

        allVacantUnits.forEach(unit => {
            html += `<tr style="border-bottom: 1px solid #eee;">
                <td style="width: 60%; padding: 6px 8px; border-right: 1px solid #eee;">${escapeHtml(unit.propertyName)}</td>
                <td style="width: 40%; padding: 6px 8px;">${escapeHtml(unit.pruNo)}</td>
            </tr>`;
        });

        html += '</tbody></table></div></div>';
        return html;
    }

    // Builds tooltip HTML listing all vacant retail units grouped by property for the given retail vacancy data.
    function getRetailVacancyTooltipHtml(data) {
        if (!data?.vacantUnits || data.vacantUnits.length === 0)
            return '<div>No retail vacancy data available</div>';

        const allVacantUnits = data.vacantUnits
            .map(unit => ({
                propertyName: propertyIdToName[unit.propertyID] ?? `Property ${unit.propertyID}`,
                pruNo: unit.propertyRentableUnitNo
            }))
            .sort((a, b) => a.propertyName.localeCompare(b.propertyName));

        let html = `<div style="max-width: 500px; border: 1px solid #ccc; border-radius: 4px; background: white; padding: 8px;">
            <table style="width: 100%; border-collapse: collapse;">
            <thead style="display: table; width: 100%; table-layout: fixed;">
                <tr style="background: #f5f5f5; border-bottom: 2px solid #ddd;">
                    <th style="width: 60%; padding: 8px; text-align: left; font-weight: bold; border-right: 1px solid #ddd;">Property Name</th>
                    <th style="width: 40%; padding: 8px; text-align: left; font-weight: bold;">Shop</th>
                </tr>
            </thead>
            </table>
            <div style="max-height: 300px; overflow-y: auto; display: block; border-bottom: 1px solid #ccc;">
                <table style="width: 100%; border-collapse: collapse;">
                <tbody style="display: table; width: 100%; table-layout: fixed;">`;

        allVacantUnits.forEach(unit => {
            html += `<tr style="border-bottom: 1px solid #eee;">
                <td style="width: 60%; padding: 6px 8px; border-right: 1px solid #eee;">${escapeHtml(unit.propertyName)}</td>
                <td style="width: 40%; padding: 6px 8px;">${escapeHtml(unit.pruNo)}</td>
            </tr>`;
        });

        html += '</tbody></table></div></div>';
        return html;
    }

    // Builds tooltip HTML showing FICA compliant and non-compliant tenant counts. 
    function getFicaComplianceTooltipHtml(data) {
        if (!data)
            return '<div>No FICA compliance data available</div>';

        const compliant = data.ficaCompliantTenants ?? 0;
        const nonCompliant = (data.totalActiveTenants ?? 0) - compliant;

        let html = `<div style="max-width: 500px; border: 1px solid #ccc; border-radius: 4px; background: white; padding: 8px;">
            <table style="width: 100%; border-collapse: collapse;">
            <thead style="display: table; width: 100%; table-layout: fixed;">
                <tr style="background: #f5f5f5; border-bottom: 2px solid #ddd;">
                    <th style="width: 60%; padding: 8px; text-align: left; font-weight: bold; border-right: 1px solid #ddd;">Status</th>
                    <th style="width: 40%; padding: 8px; text-align: left; font-weight: bold;">Count</th>
                </tr>
            </thead>
            </table>
            <div style="max-height: 300px; overflow-y: auto; display: block; border-bottom: 1px solid #ccc;">
                <table style="width: 100%; border-collapse: collapse;">
                <tbody style="display: table; width: 100%; table-layout: fixed;">
                    <tr style="border-bottom: 1px solid #eee;">
                        <td style="width: 60%; padding: 6px 8px; border-right: 1px solid #eee;">Compliant</td>
                        <td style="width: 40%; padding: 6px 8px;">${compliant}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #eee;">
                        <td style="width: 60%; padding: 6px 8px; border-right: 1px solid #eee;">Non-Compliant</td>
                        <td style="width: 40%; padding: 6px 8px;">${nonCompliant}</td>
                    </tr>
                </tbody></table></div></div>`;

        return html;
    }

    return (
        <div>
            <table>
                <thead>
                    <tr>
                        <th>Tenancy Statistics</th>
                        <th>Month<br />{monthName} {year}</th>
                        <th>Year to Date<br />{monthName} {year}</th>
                        <th>Year Ended<br />December {prevYear}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Tenant retention</td>
                        <td>
                            {tenantRetentionData != null ? `${tenantRetentionData.periodRetentionRate.toFixed(1)}%` : '-'}
                        </td>
                        <td>
                            {tenantRetentionData != null ? `${tenantRetentionData.ytdRetentionRate.toFixed(1)}%` : '-'}
                        </td>
                        <td>
                            {prevYearTenantRetentionData != null ? `${prevYearTenantRetentionData.ytdRetentionRate.toFixed(1)}%` : '_'}
                        </td>
                    </tr>
                    <tr>
                        <td>National tenants</td>
                        <td>
                            {nationalTenantStats != null ? `${Number(nationalTenantStats).toFixed(1)}%` : '-'}
                        </td>
                        <td>
                            {nationalTenantStats != null ? `${Number(nationalTenantStats).toFixed(1)}%` : '-'}
                        </td>
                        <td>
                            {prevYearNationalTenantStats != null ? `${Number(prevYearNationalTenantStats).toFixed(1)}%` : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Reversion rates</td>
                        <td>
                            {tenantReversionData != null ? `${floorToOneDecimal(tenantReversionData.periodReversion)}%` : '-'}
                        </td>
                        <td>
                            {tenantReversionData != null ? `${floorToOneDecimal(tenantReversionData.ytdReversion)}%` : '-'}
                        </td>
                        <td>
                            {tenantReversionData != null ? `${floorToOneDecimal(tenantReversionData.prevYearReversion)}%` : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Vacancies</td>
                        <td>-</td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getVacancyTooltipHtml(vacancyData)}
                        >
                            {vacancyData != null ? `${vacancyData.totalVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getVacancyTooltipHtml(prevYearVacancyData)}
                        >
                            {prevYearVacancyData != null ? `${prevYearVacancyData.totalVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Retail vacancies</td>
                        <td>-</td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getRetailVacancyTooltipHtml(retailVacancyData)}
                        >
                            {retailVacancyData != null ? `${retailVacancyData.averageRetailVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getRetailVacancyTooltipHtml(prevYearRetailVacancyData)}
                        >
                            {prevYearRetailVacancyData != null ? `${prevYearRetailVacancyData.averageRetailVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>Vacancies - Future let</td>
                        <td>-</td>
                        <td>
                            {vacancyFutureLetData != null ? `${vacancyFutureLetData.averageVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                        <td>
                            {prevYearVacancyFutureLetData != null ? `${prevYearVacancyFutureLetData.averageVacancyPercentage.toFixed(1)}%` : '-'}
                        </td>
                    </tr>
                    <tr>
                        <td>FICA compliance</td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getFicaComplianceTooltipHtml(ficaComplianceStats)}
                        >
                            {ficaComplianceStats != null ? `${ficaComplianceStats.compliancePercentage.toFixed(1)}%` : '-'}
                        </td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getFicaComplianceTooltipHtml(ficaComplianceStats)}
                        >
                            {ficaComplianceStats != null ? `${ficaComplianceStats.compliancePercentage.toFixed(1)}%` : '-'}
                        </td>
                        <td
                            data-tooltip-id="tenancy-stats-tooltip"
                            data-tooltip-content={getFicaComplianceTooltipHtml(prevYearFicaComplianceStats)}
                        >
                            {prevYearFicaComplianceStats != null ? `${prevYearFicaComplianceStats.compliancePercentage.toFixed(1)}%` : '-'}
                        </td>
                    </tr>
                </tbody>
            </table>
            <ReactTooltip
                id="tenancy-stats-tooltip"
                place="bottom"
                variant="dark"
                clickable={true}
                delayShow={0}
                delayHide={500}
                offset={5}
                float={false}
                positionStrategy="fixed"
                openEvents={{ mouseenter: true }}
                closeEvents={{ mouseleave: true }}
                globalCloseEvents={{ escape: true, resize: true, clickOutsideAnchor: true }}
                disableTooltip={(anchor) => !anchor?.getAttribute('data-tooltip-content')}
                render={({ content }) =>
                    content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                }
            />
        </div>
    );
}

export default TenancyStatsTable